import { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { usePostLoginRedirect } from '@/hooks/usePostLoginRedirect';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '@/lib/firebase';

export const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginAttempted, setLoginAttempted] = useState(false);
  const { toast } = useToast();
  const { login, user, loading: authLoading } = useAuth();
  const { onLoginSuccess } = usePostLoginRedirect();

  // Capture the ?next= parameter when the component mounts
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const next = params.get("next");
    if (next) {
      console.log("🔍 Captured ?next= parameter:", next);
      sessionStorage.setItem("returnTo", next);
    }
  }, []);

  // ✅ FIX: Redirect after user profile is loaded
  useEffect(() => {
    if (loginAttempted && !authLoading && user) {
      console.log("🔵 User profile loaded after login, triggering redirect...", user);
      onLoginSuccess(user);
      setLoginAttempted(false);
    }
  }, [loginAttempted, authLoading, user, onLoginSuccess]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Login with Firebase Auth
      await login(email, password);

      toast({
        title: "Welcome back!",
        description: "You've successfully logged in.",
      });

      // ✅ FIX: Set flag to trigger redirect after user profile loads
      setLoginAttempted(true);
    } catch (error: any) {
      let errorMessage = "An error occurred during login";

      // Gestion des erreurs Firebase Auth
      if (error.code === 'auth/user-not-found') {
        errorMessage = "No account found with this email";
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = "Incorrect password";
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = "Invalid email address";
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = "This account has been disabled";
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = "Too many failed attempts. Please try again later.";
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: "Login failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleLogin} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          className="text-sm text-muted-foreground hover:text-primary underline-offset-4 hover:underline"
          onClick={async () => {
            if (!email) {
              toast({ title: "Email required", description: "Please enter your email address first.", variant: "destructive" });
              return;
            }
            try {
              await sendPasswordResetEmail(auth, email);
              toast({ title: "Email sent", description: "Check your inbox for the password reset link." });
            } catch (err: any) {
              toast({ title: "Error", description: err.code === 'auth/user-not-found' ? "No account found with this email." : "Failed to send reset email.", variant: "destructive" });
            }
          }}
        >
          Forgot password?
        </button>
      </div>
      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? 'Signing in...' : 'Sign in'}
      </Button>
    </form>
  );
};
